import React, { useState, useEffect } from 'react';
import Slider from 'react-slick';
import 'slick-carousel/slick/slick.css';
import 'slick-carousel/slick/slick-theme.css';
import './EquipmentCarousel.css';
import { FaCog, FaExclamationCircle } from 'react-icons/fa';

// Custom arrow components
const CustomPrevArrow = ({ onClick }) => (
  <button type="button" className="equipment-carousel-arrow" aria-label="Previous equipment" disabled={!onClick}
    onClick={onClick}
    style={{
      position: 'absolute',
      left: '-20px',
      top: '50%',
      transform: 'translateY(-50%)',
      backgroundColor: '#f3f4f6',
      border: '1px solid #d1d5db',
      borderRadius: '50%',
      width: '40px',
      height: '40px',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      cursor: 'pointer',
      fontSize: '16px',
      color: '#374151',
      boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
      transition: 'all 0.2s',
      zIndex: 10
    }}
    onMouseEnter={(e) => {
      e.target.style.backgroundColor = '#e5e7eb';
      e.target.style.borderColor = '#9ca3af';
    }}
    onMouseLeave={(e) => {
      e.target.style.backgroundColor = '#f3f4f6';
      e.target.style.borderColor = '#d1d5db';
    }}
  >
    ←
  </button>
);

const CustomNextArrow = ({ onClick }) => (
  <button type="button" className="equipment-carousel-arrow" aria-label="Next equipment" disabled={!onClick}
    onClick={onClick}
    style={{
      position: 'absolute',
      right: '-20px',
      top: '50%',
      transform: 'translateY(-50%)',
      backgroundColor: '#f3f4f6',
      border: '1px solid #d1d5db',
      borderRadius: '50%',
      width: '40px',
      height: '40px',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      cursor: 'pointer',
      fontSize: '16px',
      color: '#374151',
      boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
      transition: 'all 0.2s',
      zIndex: 10
    }}
    onMouseEnter={(e) => {
      e.target.style.backgroundColor = '#e5e7eb';
      e.target.style.borderColor = '#9ca3af';
    }}
    onMouseLeave={(e) => {
      e.target.style.backgroundColor = '#f3f4f6';
      e.target.style.borderColor = '#d1d5db';
    }}
  >
    →
  </button>
);

// Equipment Card Component
const EquipmentCard = ({ item, onNavigate }) => {
  const handleClick = () => {
    onNavigate(`/mechanical-room/equipment/${item.id}`);
  };

  return (
    <div
      data-equipment-id={item.id}
      data-equipment-name={item.name}
      className="equipment-card"
      style={{
        backgroundColor: '#fff',
        borderRadius: '8px',
        padding: '1.5rem',
        boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
        cursor: 'pointer',
        transition: 'transform 0.2s, box-shadow 0.2s',
        height: '100%'
      }}
      onClick={handleClick}
    >
      {/* Header with title and active issues pill */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
          <div style={{
            width: '20px',
            height: '20px',
            backgroundColor: '#dbeafe',
            borderRadius: '4px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            fontSize: '12px',
            color: '#3b82f6'
          }}>
            <FaCog />
          </div>
          <h5 style={{ margin: 0, fontWeight: '600', color: '#374151', fontSize: '0.9rem' }}>{item.name}</h5>
        </div>
        {/* Active issues pill in top right */}
        {item.issuesCount > 0 && (
          <span style={{
            backgroundColor: '#fee2e2',
            color: '#b91c1c',
            borderRadius: '999px',
            padding: '4px 12px',
            fontSize: '0.7rem',
            fontWeight: '600',
            display: 'inline-flex',
            alignItems: 'center',
            gap: '0.25rem',
            whiteSpace: 'nowrap'
          }}>
            <FaExclamationCircle style={{ fontSize: '0.7rem' }} />
            {item.issuesCount} active issue{item.issuesCount > 1 ? 's' : ''}
          </span>
        )}
      </div>
      
      {/* Equipment image in center */}
      <div style={{ display: 'flex', justifyContent: 'center', marginBottom: '1rem' }}>
        <img
          src={item.photoUrl || 'https://via.placeholder.com/300x140/f2f2f2/999999?text=Equipment+Image'}
          alt={item.name}
          style={{
            width: '100%',
          height: '200px', // Increased height to make carousel cards taller
            objectFit: 'cover',
            borderRadius: '8px'
          }}
          onError={(e) => {
            e.target.src = 'https://via.placeholder.com/300x140/f2f2f2/999999?text=Equipment+Image';
          }}
        />
      </div>

      {/* View Details Button */}
      <button
        style={{
          width: '100%',
          padding: '0.75rem',
          backgroundColor: '#f3f4f6',
          color: '#374151',
          border: 'none',
          borderRadius: '6px',
          fontWeight: '600',
          cursor: 'pointer',
          fontSize: '0.875rem',
          marginBottom: '0.5rem',
          transition: 'background-color 0.2s'
        }}
        onMouseEnter={(e) => {
          e.target.style.backgroundColor = '#e5e7eb';
        }}
        onMouseLeave={(e) => {
          e.target.style.backgroundColor = '#f3f4f6';
        }}
      >
        View Details →
      </button>

      {/* Last service date */}
      <div style={{ color: '#6b7280', fontSize: '0.75rem', textAlign: 'center' }}>
        Last service: {item.lastServiceDate || 'Sept 10, 2025'}
      </div>
    </div>
  );
};

function EquipmentCarousel({ equipment, onNavigate }) {
  const [itemsPerRow, setItemsPerRow] = useState(() => window.innerWidth < 768 ? 1 : window.innerWidth < 1024 ? 2 : 3);
  const [currentSlide, setCurrentSlide] = useState(0);
  
  
  useEffect(() => {
    const updateItemsPerRow = () => {
      if (window.innerWidth < 768) {
        setItemsPerRow(1); // Mobile: 1 per row
      } else if (window.innerWidth < 1024) {
        setItemsPerRow(2); // Tablet: 2 per row
      } else {
        setItemsPerRow(3); // Desktop: 3 per row
      }
    };

    updateItemsPerRow();
    window.addEventListener('resize', updateItemsPerRow);
    return () => window.removeEventListener('resize', updateItemsPerRow);
  }, []);

  useEffect(() => { setCurrentSlide(0); }, [itemsPerRow]);

  if (equipment.length === 0) return null;

  // Group equipment into rows for 2-row layout
  const groupEquipmentIntoRows = (equipment, itemsPerRow) => {
    const rows = [];
    const perSlide = itemsPerRow === 1 ? 1 : itemsPerRow * 2;
    for (let i = 0; i < equipment.length; i += perSlide) {
      const slideEquipment = equipment.slice(i, i + perSlide);
      rows.push(slideEquipment);
    }
    return rows;
  };

  const equipmentRows = groupEquipmentIntoRows(equipment, itemsPerRow);

  // Responsive settings for react-slick
  const settings = {
    dots: itemsPerRow > 1,
    afterChange: setCurrentSlide,
    infinite: false,
    speed: 500,
    slidesToShow: 1, // Show 1 "slide" which contains 2 rows
    slidesToScroll: 1,
    adaptiveHeight: true,
    // Custom arrows
    prevArrow: <CustomPrevArrow />,
    nextArrow: <CustomNextArrow />,
  };

  return (
    <div className="equipment-carousel" style={{ padding: '0' }}>
      <Slider key={itemsPerRow} {...settings}>
        {equipmentRows.map((slideEquipment, slideIndex) => (
          <div key={slideIndex}>
            {/* Create 2 rows for this slide */}
            <div style={{ display: 'grid', gap: '1rem', padding: '0 0.75rem' }}>
              {/* First row */}
              <div style={{ 
                display: 'grid', 
                gridTemplateColumns: `repeat(${itemsPerRow}, 1fr)`, 
                gap: '1rem' 
              }}>
                {slideEquipment.slice(0, itemsPerRow).map((item, index) => (
                  <EquipmentCard key={`${slideIndex}-row1-${index}-${item.id}`} item={item} onNavigate={onNavigate} />
                ))}
              </div>
              
              {/* Second row is desktop/tablet only. */}
              {itemsPerRow > 1 && <div style={{
                display: 'grid', 
                gridTemplateColumns: `repeat(${itemsPerRow}, 1fr)`, 
                gap: '1rem' 
              }}>
                {slideEquipment.slice(itemsPerRow, itemsPerRow * 2).map((item, index) => (
                  <EquipmentCard key={`${slideIndex}-row2-${index}-${item.id}`} item={item} onNavigate={onNavigate} />
                ))}
              </div>}
            </div>
          </div>
        ))}
      </Slider>
      {itemsPerRow === 1 && <p className="equipment-carousel-position" aria-live="polite">{Math.min(currentSlide + 1, equipmentRows.length)} of {equipmentRows.length} equipment · Swipe to browse</p>}
    </div>
  );
}

export default EquipmentCarousel;